import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Check, Circle, Download, Eye, EyeOff, HardDriveDownload, X } from "lucide-react";
import { useEffect, useState } from "react";
import { create } from "zustand";

import { api, ApiError } from "../../api/client";
import type { BackupBlocker, BackupReason, BackupStatus, BackupStepId, BackupSummary, BackupWarning } from "../../api/types";
import { attentionSelector, designate } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Checkbox } from "../../components/Checkbox";
import { Dialog } from "../../components/Dialog";
import { Spinner } from "../../components/EmptyState";
import { ProgressBar } from "../../components/Gauge";
import { PropRow } from "../../components/panel";
import { motion, Presence } from "../../components/presence";
import { Button, Field, IconButton, inputClass } from "../../components/ui";
import { Waiting } from "../../components/Waiting";
import { lang, t } from "../../i18n/i18n";
import { plural } from "../../plural";
import { isPreprod, useEnvironmentValue } from "../environment";
import { formatBytes } from "../../map/zones/storage";
import { blockerText, formatParis, formatParisTime, missingField, missingText, PASSWORD_MAX, requestErrorText } from "./promotion";

/** Fenêtre « Sauvegarder Generex-Web » (registre F43, docs/11 § 16), à côté de « Promouvoir… » sur l'accueil de la
 *  préproduction. Elle montre la taille estimée de la sauvegarde et la place libre du serveur, propose de joindre la
 *  base de la préproduction, puis demande le mot de passe de promotion, le même que pour « Promouvoir ». Le serveur
 *  vérifie le mot de passe (« Vérification du mot de passe… »), puis assemble la sauvegarde sans arrêter la
 *  production : six étapes, avancement en octets pendant l'écriture de l'archive. À la fin, « Télécharger » remet au
 *  navigateur une archive qui s'ouvre en un dossier unique « Sauvegarde Generex-Web AAAA-MM-JJ ». Le lien n'existe que
 *  dans l'onglet qui a lancé la sauvegarde (jeton gardé pour la session de l'onglet), une seule fois : l'archive est
 *  effacée du serveur dès son téléchargement complet, ou au bout de 24 h. */

export const BACKUP_KEY = ["preprod", "backup"] as const;
export const POLL_MS = 1500;
export const STEP_ORDER: readonly BackupStepId[] = ["checks", "databases", "files", "ai", "archive", "verify"];
export const STEP_LABELS: Record<BackupStepId, string> = {
  checks: "Contrôles du serveur",
  databases: "Bases de données (production, licences)",
  files: "Fichiers du serveur (code, site, réglages, licences)",
  ai: "Intelligence artificielle (modèle affiné)",
  archive: "Écriture de l'archive",
  verify: "Relecture de l'archive",
};
const ACTIVE = new Set(["pending", "queued", "running"]);
export const isActiveBackup = (s: BackupStatus | null | undefined): boolean => Boolean(s && ACTIVE.has(s.state));

const useDialog = create<{ open: boolean }>(() => ({ open: false }));
export const openBackupDialog = () => useDialog.setState({ open: true });

// --- Jeton du téléchargement : rendu au seul onglet qui a lancé la sauvegarde ------------------------------------------

const TOKEN_KEY = "generex.sauvegarde.jeton";
export interface Ticket {
  request_id: string;
  token: string;
}
export const useTicket = create<{ ticket: Ticket | null }>(() => ({ ticket: readTicket() }));
function readTicket(): Ticket | null {
  try {
    const raw = sessionStorage.getItem(TOKEN_KEY);
    const d = raw ? (JSON.parse(raw) as Ticket) : null;
    return d && typeof d.request_id === "string" && typeof d.token === "string" ? d : null;
  } catch {
    return null;
  }
}
export function keepTicket(ticket: Ticket | null): void {
  useTicket.setState({ ticket });
  try {
    if (ticket) sessionStorage.setItem(TOKEN_KEY, JSON.stringify(ticket));
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // stockage de l'onglet indisponible : le jeton reste en mémoire tant que la page est ouverte
  }
}

// --- Textes ---------------------------------------------------------------------------------------------------------

export const sizeText = (bytes: number | null | undefined): string => formatBytes(bytes ?? 0, lang() === "en" ? "en" : "fr");

export function backupBlockerText(blocker: BackupBlocker, lockedUntil?: string | null): string {
  switch (blocker) {
    case "not_configured":
      return blockerText("not_configured");
    case "locked":
      return lockedUntil
        ? t("Cinq mots de passe faux en une heure : sauvegardes et promotions bloquées jusqu'à {time}.", { time: formatParisTime(lockedUntil) })
        : t("Cinq mots de passe faux en une heure : sauvegardes et promotions bloquées pendant une heure.");
    case "running":
      return t("Une sauvegarde est déjà en cours : suivez-la ici.");
    case "promotion_running":
      return t("Une promotion est en cours : lancez la sauvegarde une fois la promotion finie.");
    case "busy":
      return t("Une opération est en cours sur le serveur (installation, recopie ou restauration) : réessayez dans quelques minutes.");
    case "nightly_backup":
      return t("La sauvegarde nocturne de la production est en cours ou va commencer : réessayez dans une heure.");
  }
}

export function backupReasonText(reason: BackupReason | null | undefined, status?: Pick<BackupStatus, "remaining" | "locked_until">): string {
  switch (reason) {
    case "code":
      return typeof status?.remaining === "number" && status.remaining > 0
        ? t("Mot de passe de promotion incorrect : encore {tries} avant un blocage d'une heure.", {
            tries: plural(status.remaining, t("essai"), t("essais")),
          })
        : t("Mot de passe de promotion incorrect : vérifiez-le, puis recommencez.");
    case "locked":
      return backupBlockerText("locked", status?.locked_until);
    case "not_configured":
      return blockerText("not_configured");
    case "expired":
      return t("La demande a mis trop de temps à parvenir au service de promotion : recommencez.");
    case "invalid":
      return t("Demande illisible : rechargez la page, puis recommencez.");
    case "running":
      return backupBlockerText("running");
    case "busy":
      return backupBlockerText("busy");
    case "nightly_backup":
      return backupBlockerText("nightly_backup");
    case "disk":
      return t("Place insuffisante sur le serveur pour assembler la sauvegarde : libérez de la place, puis recommencez.");
    case "database":
      return t("Les bases de données n'ont pas pu être copiées : réessayez dans un instant.");
    case "files":
      return t("Les fichiers du serveur n'ont pas pu être lus : réessayez dans un instant.");
    case "ai":
      return t("Le modèle d'intelligence artificielle n'a pas pu être copié : réessayez dans un instant.");
    case "archive":
      return t("L'archive n'a pas pu être écrite ou relue : réessayez dans un instant.");
    case "interrupted":
      return t("La sauvegarde a été interrompue (arrêt ou redémarrage du serveur) : rien n'a été gardé, recommencez.");
    default:
      return t("La sauvegarde a échoué pour une raison imprévue : réessayez dans un instant.");
  }
}

export function warningText(warning: BackupWarning): string {
  switch (warning) {
    case "no_git":
      return t("Le dépôt du code du serveur est absent : le code en service est bien sauvegardé, mais sans son historique.");
    case "commit_missing":
      return t("La révision en service n'est pas dans le dépôt du code du serveur : le code en service est bien sauvegardé.");
    case "no_generex_3b":
      return t("Le modèle affiné de l'assistant n'a pas été trouvé : il manquera à la réinstallation.");
    case "no_hf":
      return t("La base d'entraînement des ENI personnalisés est absente : leur entraînement sera à réinstaller à la main.");
    case "no_bellicom":
      return t("La base du serveur de licences est absente du serveur : elle n'est pas dans la sauvegarde.");
    case "ollama_unreachable":
      return t("Le service d'intelligence artificielle ne répondait pas : la liste des modèles à retélécharger peut manquer.");
  }
}

/** Erreur de l'API (avant le service de promotion), en clair. */
export function backupErrorText(error: unknown): { text: string; field?: "password" } {
  if (error instanceof ApiError) {
    if (error.code === "BACKUP_BLOCKED") {
      const blocker = typeof error.detail?.blocker === "string" ? (error.detail.blocker as BackupBlocker) : null;
      const until = typeof error.detail?.locked_until === "string" ? error.detail.locked_until : null;
      return { text: blocker ? backupBlockerText(blocker, until) : t("Sauvegarde impossible pour le moment.") };
    }
    if (error.code === "BACKUP_GONE") return { text: t("Cette archive n'est plus sur le serveur (téléchargée, périmée ou remplacée) : lancez une nouvelle sauvegarde.") };
    if (error.code === "BACKUP_TOKEN") return { text: t("Le lien de téléchargement n'est valable que dans l'onglet qui a lancé la sauvegarde.") };
    if (error.code === "PROMOTION_ADMIN_ONLY") return { text: t("La sauvegarde est réservée aux administrateurs de l'unité.") };
    if (error.status === 429) return { text: t("Trop de demandes de sauvegarde à la suite : patientez une minute.") };
  }
  return requestErrorText(error);
}

/** Avancement (0 à 1) : étapes faites, et octets écrits pendant l'écriture de l'archive (l'essentiel du temps). */
export function backupProgress(status: BackupStatus): number {
  const done = status.steps.filter((s) => s.state === "done").length;
  if (status.step === "archive" && status.bytes_total) {
    const part = Math.min(1, (status.bytes_done ?? 0) / status.bytes_total);
    return (4 + part * 1.8) / 6;
  }
  return done / STEP_ORDER.length;
}

// --- Encadré de l'accueil -------------------------------------------------------------------------------------------

/** Bouton de l'encadré « Promotion en production », à côté de « Promouvoir… ». */
export function BackupButton() {
  const summary = useQuery<BackupSummary>({ queryKey: BACKUP_KEY, queryFn: api.preprod.backup, refetchOnWindowFocus: true });
  return (
    <Button variant="secondary" data-attention="backup-open" disabled={!summary.data} onClick={openBackupDialog}>
      <HardDriveDownload size={14} /> {t("Sauvegarder Generex-Web…")}
    </Button>
  );
}

/** Ligne de l'encadré : sauvegarde en cours, prête (jusqu'à quand), ou aucune. */
export function BackupLine() {
  const summary = useQuery<BackupSummary>({
    queryKey: BACKUP_KEY,
    queryFn: api.preprod.backup,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => (isActiveBackup(query.state.data?.status) ? 5000 : false),
  });
  const status = summary.data?.status ?? null;
  return (
    <PropRow label={t("Sauvegarde complète")}>
      {summary.isError ? (
        t("Illisible")
      ) : !summary.data ? (
        <Waiting>{t("Lecture…")}</Waiting>
      ) : isActiveBackup(status) ? (
        <Waiting>{t("Sauvegarde en cours…")}</Waiting>
      ) : status?.state === "ready" ? (
        t("Prête, téléchargeable jusqu'au {date}", { date: formatParis(status.expires_at) })
      ) : status?.state === "downloaded" && status.ended_at ? (
        t("Téléchargée le {date}", { date: formatParis(status.ended_at) })
      ) : (
        t("Aucune en attente")
      )}
    </PropRow>
  );
}

/** Fenêtre, à poser à la racine de l'accueil (préproduction seulement). */
export function BackupDialogHost() {
  const preprod = useEnvironmentValue(isPreprod);
  const open = useDialog((s) => s.open);
  const summary = useQuery<BackupSummary>({ queryKey: BACKUP_KEY, queryFn: api.preprod.backup, enabled: false });
  useEffect(() => () => useDialog.setState({ open: false }), []);
  const shown = preprod && open && Boolean(summary.data);
  return (
    <Presence show={shown}>
      {shown && summary.data && <BackupDialog summary={summary.data} onClose={() => useDialog.setState({ open: false })} />}
    </Presence>
  );
}

// --- Fenêtre ---------------------------------------------------------------------------------------------------------

export function BackupDialog({
  summary,
  onClose,
  ticket: given,
}: {
  summary: BackupSummary;
  onClose: () => void;
  /** Jeton de l'onglet (essais seulement ; sinon celui gardé pour la session de l'onglet). */
  ticket?: Ticket | null;
}) {
  const queryClient = useQueryClient();
  const kept = useTicket((s) => s.ticket);
  const ticket = given === undefined ? kept : given;
  const [password, setPassword] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [withPreprod, setWithPreprod] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [tracked, setTracked] = useState<BackupStatus | null>(null);
  const [stalled, setStalled] = useState(false);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: BACKUP_KEY });

  // Statut montré : celui de la demande suivie, sinon le dernier travail publié (sauvegarde en cours ou prête).
  const status = tracked ?? summary.status;
  const active = isActiveBackup(status);
  const requestId = active && status ? status.request_id : null;

  const send = useMutation({
    mutationFn: () => api.preprod.backupRequest({ password, include_preprod: withPreprod }),
    onSuccess: (reply) => {
      setPassword("");
      setStalled(false);
      keepTicket({ request_id: reply.request_id, token: reply.token });
      setTracked(reply.status);
    },
    onError: (error) => {
      setPassword("");
      if (backupErrorText(error).field) designate(attentionSelector("backup-password"), "field");
    },
  });

  // Suivi de la demande puis du travail, jusqu'à l'issue publiée par le service de promotion.
  useEffect(() => {
    if (!requestId) return;
    let stop = false;
    const timer = window.setTimeout(async () => {
      try {
        const reply = await api.preprod.backupStatus(requestId);
        if (stop) return;
        setStalled(reply.stalled);
        setTracked(reply.status ? reply.status : (s) => (s ? { ...s } : s));
        if (reply.status && !isActiveBackup(reply.status)) refresh();
      } catch {
        if (!stop) setTracked((s) => (s ? { ...s } : s));
      }
    }, POLL_MS);
    return () => {
      stop = true;
      window.clearTimeout(timer);
    };
  }, [requestId, status]);
  useEffect(() => {
    if (status?.state === "refused" && status.reason === "code") designate(attentionSelector("backup-password"), "field");
  }, [status?.state, status?.reason]);

  const download = useMutation({
    mutationFn: (given: Ticket) => api.preprod.backupTicket(given),
    onSuccess: (reply) => {
      // Lien du même site : le navigateur enregistre l'archive (pièce jointe) sans quitter la page.
      const link = document.createElement("a");
      link.href = `/api${reply.url.replace(/^\/api/, "")}`;
      link.download = reply.name;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(refresh, 5000);
    },
  });

  const blockers = summary.blockers.filter((b) => b !== "running" || !active);
  const blocked = blockers.length > 0;
  const checking = send.isPending || status?.state === "pending";
  const ready = status?.state === "ready";
  const mine = Boolean(ticket && status && ticket.request_id === status.request_id);
  const showForm = !active && !ready;
  const estimate = summary.estimate;
  const needed = estimate ? (withPreprod ? estimate.bytes_with_preprod : estimate.bytes) : null;

  const submit = () => {
    if (checking) return;
    if (blocked) {
      designate(attentionSelector("backup-blocker"), "ring");
      return;
    }
    const missing = missingField(password);
    if (missing) {
      setHint(missingText(missing));
      designate(attentionSelector("backup-password"), "field");
      return;
    }
    setHint(null);
    setRevealed(false);
    setTracked(null);
    send.mutate();
  };
  const reveal = revealed ? t("Masquer le mot de passe") : t("Afficher le mot de passe");

  return (
    <Dialog
      title={t("Sauvegarder Generex-Web")}
      subtitle={t("Tout ce qu'il faut pour réinstaller Generex-Web sur un nouveau serveur, dans un dossier unique daté.")}
      width={620}
      onClose={onClose}
      dirty={showForm && password !== ""}
      footer={
        ready || active || status?.state === "downloaded" ? (
          <>
            {ready && (
              <Button variant="secondary" onClick={() => setTracked({ ...(status as BackupStatus), state: "expired" })}>
                {t("Nouvelle sauvegarde")}
              </Button>
            )}
            <Button variant="primary" onClick={onClose}>
              {t("Fermer")}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              {checking ? t("Fermer") : t("Annuler")}
            </Button>
            <Button
              data-backup-submit
              disabled={checking}
              blocked={blocked || missingField(password) !== null}
              onBlocked={submit}
              onClick={submit}
            >
              {checking ? <Spinner decorative /> : <HardDriveDownload size={14} />}{" "}
              {checking ? <Waiting>{t("Vérification du mot de passe…")}</Waiting> : t("Lancer la sauvegarde")}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4 px-4 py-4">
        {showForm && (
          <div className={clsx("space-y-4", motion("fade"))}>
            <p className="text-ui leading-snug text-ink-soft">
              {t("Code, site, bases de données, réglages et secrets, serveur de licences, modèle d'intelligence artificielle affiné, et l'exécutable qui réinstalle tout depuis le Terminal du Mac. La production reste en service pendant la sauvegarde.")}
            </p>
            <div className="rounded-lg border border-line px-3">
              <PropRow label={t("Taille estimée")}>
                {needed !== null ? t("environ {size}", { size: sizeText(needed) }) : <Waiting>{t("Estimation…")}</Waiting>}
              </PropRow>
              <PropRow label={t("Place libre sur le serveur")}>
                {estimate?.free_bytes != null ? sizeText(estimate.free_bytes) : "—"}
              </PropRow>
              <PropRow label={t("Validité du lien")}>{t("24 heures, un seul téléchargement")}</PropRow>
            </div>
            <Checkbox
              checked={withPreprod}
              onChange={setWithPreprod}
              disabled={checking}
              label={t("Joindre la base de la préproduction")}
              detail={t("Facultatif : à la réinstallation, la préproduction est reclonée de la production.")}
            />
            <Callout tone="warn">
              {t("Le dossier téléchargé contient des secrets en clair (mots de passe, clés, base des licences) : gardez-le en lieu sûr et ne le partagez pas.")}
            </Callout>
            <Field
              label={t("Mot de passe de promotion")}
              hint={t("Le même que pour « Promouvoir ». Cinq erreurs en une heure bloquent pendant une heure les sauvegardes, les promotions et le Terminal.")}
            >
              <span className="relative block">
                <input
                  type={revealed ? "text" : "password"}
                  data-attention="backup-password"
                  className={clsx(inputClass, "pr-9")}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setHint(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      submit();
                    }
                  }}
                  autoComplete="one-time-code"
                  data-1p-ignore
                  data-lpignore="true"
                  autoCapitalize="none"
                  autoCorrect="off"
                  maxLength={PASSWORD_MAX}
                  spellCheck={false}
                  disabled={checking || blocked}
                />
                <IconButton
                  size="sm"
                  className="absolute top-1/2 right-0.5 -translate-y-1/2"
                  onClick={(e) => {
                    e.preventDefault();
                    setRevealed((r) => !r);
                  }}
                  title={reveal}
                  aria-label={reveal}
                  aria-pressed={revealed}
                  disabled={checking || blocked}
                >
                  <span key={revealed ? "shown" : "hidden"} className={clsx("flex", motion("fade"))}>
                    {revealed ? <EyeOff size={14} aria-hidden /> : <Eye size={14} aria-hidden />}
                  </span>
                </IconButton>
              </span>
            </Field>
          </div>
        )}

        {status && (active || ready || status.state === "failed" || status.state === "interrupted") && status.state !== "pending" && (
          <BackupProgress status={status} />
        )}

        <Presence show={ready}>
          {ready && status && (
            <Callout tone="ok" title={t("Sauvegarde prête : {size}", { size: sizeText(status.size) })}>
              <span className="block">
                {t("Le navigateur l'enregistre dans le dossier des téléchargements ; un double-clic l'ouvre en un dossier « {name} », qui contient l'exécutable de réinstallation et son mode d'emploi (LISEZMOI).", { name: status.name ?? "" })}
              </span>
              <span className="mt-1 block text-meta text-ink-muted">
                {t("Un seul téléchargement, jusqu'au {date} : l'archive est effacée du serveur dès qu'elle est entièrement téléchargée.", { date: formatParis(status.expires_at) })}
              </span>
              {mine ? (
                <Button className="mt-2" data-backup-download disabled={download.isPending} onClick={() => ticket && download.mutate(ticket)}>
                  {download.isPending ? <Spinner decorative /> : <Download size={14} />}{" "}
                  {download.isPending ? <Waiting>{t("Préparation du téléchargement…")}</Waiting> : t("Télécharger")}
                </Button>
              ) : (
                <span className="mt-1 block text-meta text-ink-muted">
                  {t("Le lien de téléchargement n'est valable que dans l'onglet qui a lancé la sauvegarde : « Nouvelle sauvegarde » la refait ici.")}
                </span>
              )}
            </Callout>
          )}
        </Presence>
        <Presence show={ready && Boolean(status?.warnings.length)}>
          {ready && status && status.warnings.length > 0 && (
            <Callout tone="warn" title={t("À savoir")}>
              <ul className="list-disc space-y-0.5 pl-4">
                {status.warnings.map((w) => (
                  <li key={w}>{warningText(w)}</li>
                ))}
              </ul>
            </Callout>
          )}
        </Presence>
        <Presence show={status?.state === "downloaded"}>
          {status?.state === "downloaded" && (
            <Callout tone="ok" title={t("Archive téléchargée")}>
              {t("Elle a été effacée du serveur. Gardez le dossier en lieu sûr : pour tout réinstaller, double-cliquez REINSTALLER-GENEREX-WEB.command.")}
            </Callout>
          )}
        </Presence>
        <Presence show={status?.state === "failed" || status?.state === "interrupted" || status?.state === "refused"}>
          {status && (status.state === "failed" || status.state === "interrupted" || status.state === "refused") && (
            <Callout tone="danger" title={status.state === "refused" ? t("Sauvegarde refusée") : t("Sauvegarde échouée")}>
              {backupReasonText(status.reason, status)} {t("La production n'a pas changé.")}
            </Callout>
          )}
        </Presence>
        <Presence show={showForm && blocked}>
          {showForm && blocked && blockers[0] && (
            <div data-attention="backup-blocker" className="rounded-lg">
              <Callout tone={blockers[0] === "locked" ? "warn" : "info"}>{backupBlockerText(blockers[0], summary.locked_until)}</Callout>
            </div>
          )}
        </Presence>
        <Presence show={Boolean(checking)}>
          {checking && (
            <div role="status">
              <Callout tone={stalled ? "warn" : "info"}>
                {stalled
                  ? t("Le service de promotion ne répond pas : la demande attend d'être prise en charge. Rien n'a changé en production.")
                  : t("Vérification du mot de passe par le service de promotion… Rien n'a changé en production.")}
              </Callout>
            </div>
          )}
        </Presence>
        <Presence show={send.isError && !checking}>{send.isError && !checking && <Callout tone="danger">{backupErrorText(send.error).text}</Callout>}</Presence>
        <Presence show={download.isError}>{download.isError && <Callout tone="danger">{backupErrorText(download.error).text}</Callout>}</Presence>
        <Presence show={Boolean(hint) && !send.isError}>{hint && !send.isError && <Callout tone="info">{hint}</Callout>}</Presence>
      </div>
    </Dialog>
  );
}

function BackupProgress({ status }: { status: BackupStatus }) {
  const active = isActiveBackup(status);
  return (
    <div className={clsx("space-y-3", motion("fade"))}>
      {active && (
        <div className="space-y-1.5" role="status">
          <p className="flex min-h-8 items-center text-ui font-medium text-run">
            <Waiting>{t("Sauvegarde en cours, étape {n} sur {total}…", { n: Math.max(1, STEP_ORDER.indexOf(status.step ?? "checks") + 1), total: STEP_ORDER.length })}</Waiting>
          </p>
          <ProgressBar value={backupProgress(status)} tone="run" label={t("Avancement de la sauvegarde")} />
          {status.step === "archive" && status.bytes_total ? (
            <p className="text-meta text-ink-muted tabular-nums">
              {t("{done} écrits sur {total}", { done: sizeText(status.bytes_done), total: sizeText(status.bytes_total) })}
            </p>
          ) : null}
        </div>
      )}
      <ol className="rounded-lg border border-line px-3" aria-label={t("Étapes de la sauvegarde")}>
        {STEP_ORDER.map((id) => {
          const state = status.steps.find((s) => s.id === id)?.state ?? "todo";
          return (
            <li key={id} className="flex min-h-8 items-center gap-2.5 border-b border-line py-1.5 last:border-b-0">
              <StepIcon state={state} />
              <span className={clsx("min-w-0 flex-1 text-ui", state === "todo" || state === "skipped" ? "text-ink-muted" : "text-ink", state === "running" && "font-medium")}>
                {t(STEP_LABELS[id])}
              </span>
              {state === "failed" && <span className="shrink-0 text-meta text-danger">{t("Échec")}</span>}
            </li>
          );
        })}
      </ol>
      {active && <p className="text-meta text-ink-muted">{t("Vous pouvez fermer cette fenêtre : la sauvegarde continue sur le serveur ; rouvrez-la depuis cet onglet pour la télécharger.")}</p>}
    </div>
  );
}

function StepIcon({ state }: { state: BackupStatus["steps"][number]["state"] }) {
  if (state === "running") return <Spinner decorative tone="run" />;
  if (state === "done") return <Check size={14} aria-hidden className="shrink-0 text-ok" />;
  if (state === "failed") return <X size={14} aria-hidden className="shrink-0 text-danger" />;
  return <Circle size={14} aria-hidden className="shrink-0 text-line-strong" />;
}
